import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { APP_CONFIG } from '@mnz-core/injectors/app-configuration';
import { IAppConfiguration } from '@mnz-core/models/app-configuration';
import { IResponse } from '@mnz-core/models/response';
import { Observable } from 'rxjs';
import { delay, map, tap } from 'rxjs/operators';
import { ResponseErrorLevel } from '@mnz-core/models/_enums';

/**
 * An HTTP client wrapper service
 * @description It's preferable to use this service to make all HTTP API calls, incercepts errors from server.
 */
@Injectable({
  providedIn: 'root',
})
export class HttpClientService {
  private readonly _mockApi = environment.api.mock && !environment.production;

  constructor(@Inject(APP_CONFIG) private readonly _config: IAppConfiguration, private readonly _http: HttpClient) {}

  /**
   * Perform a POST request to the given url
   * @param url The url to perform the request on
   * @param body The body
   * e.g. Bearer <token>
   */
  public post<T extends IResponse<any>>(url: string, body?: any, customOptions?: {}): Observable<T> {
    const options = this.addDefaultOptions(customOptions);

    return this._mockApi
      ? this._handleMockResponse(url)
      : this._http.post<T>(url, body, options);
  }

  /**
   * Perform a GET request to the given url
   * @param url The url to perform the request on
   * e.g. Bearer <token>
   */
  public get<T extends IResponse<any>>(url: string, customOptions?: {}): Observable<T> {
    const options = this.addDefaultOptions(customOptions);

    return this._mockApi
      ? this._handleMockResponse(url)
      : this._http.get<T>(url, options);
  }

  private addDefaultOptions(customOptions?: {}): {} {
    const options = {
      headers: new HttpHeaders({ 'Ocp-Apim-Subscription-Key': this._config.api_subscription_key }),
      ...customOptions,
    };

    return options;
  }

  private _handleMockResponse<T>(url: string): Observable<T> {
    const { api } = environment;
    const { staticsPath, url_api_base } = this._config;

    const strippedUrl = url.replace(url_api_base, String());
    const path = strippedUrl.split('?').shift();

    return this._http.get<T>(`${staticsPath}/assets/api/mock-api.json`).pipe(
      map((response) => response[path]),
      delay(api.delayMs)
    );
  }
}
